import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Home, 
  Search, 
  BookOpen, 
  Heart, 
  ShoppingBag, 
  User, 
  LogOut,
  PlusCircle,
  Plus,
  ChevronDown,
  ThumbsUp,
  Archive,
  Award,
  Users,
  MessageSquare,
  Bell,
  Store,
  Settings,
  Newspaper,
  TrendingUp,
  LayoutGrid,
  ChevronRight,
  ShieldCheck,
  Edit3,
  Moon,
  Sun,
  Puzzle
} from 'lucide-react';
import CommunityService from '../lib/services/communityService';
import SEO from './SEO';

// Criar uma instância do serviço
const communityService = new CommunityService();

interface LayoutProps {
  children: React.ReactNode;
}

// Novo componente para o item do Acordeão
interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  initiallyOpen?: boolean;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, children, initiallyOpen = false }) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  return (
    <div className="py-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none"
      >
        <span className="font-medium uppercase text-xs text-gray-500 dark:text-gray-400">{title}</span>
        <ChevronRight className={`w-4 h-4 transform transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && (
        <div className="pl-4 border-l-2 border-gray-200 dark:border-gray-600 ml-4">
          {children}
        </div>
      )}
    </div>
  );
};

export function Layout({ children }: LayoutProps) {
  const { user, isAuthenticated, isAdmin, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [userMenuOpen, setUserMenuOpen] = React.useState(false);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const navLinks = [
    { to: '/', label: 'Início', icon: Home, end: true },
    { to: '/search', label: 'Buscar', icon: Search, end: false },
    { to: '/ranking', label: 'Ranking', icon: Award, end: true },
    { to: '/feed', label: 'Feed', icon: Newspaper, requiresAuth: true, end: true },
    { to: '/recommendations', label: 'Recomendações', icon: ShoppingBag, end: true },
    { to: '/stores', label: 'Lojas Parceiras', icon: Store, end: true },
    { to: '/market', label: 'Mercado', icon: Store, requiresAuth: true, end: true },
  ];

  const authLinks = [
    { to: '/collection', label: 'Minha Coleção', icon: BookOpen, adminOnly: false, end: true },
    { to: '/wishlist', label: 'Lista de Desejos', icon: Heart, adminOnly: false, end: true },
    { to: '/collection/played', label: 'Jogos Jogados', icon: PlusCircle, adminOnly: false, end: true },
    { to: '/feedback', label: 'Meus Feedbacks', icon: Archive, adminOnly: false, end: true },
    { to: '/admin', label: 'Painel Admin', icon: ShieldCheck, adminOnly: true, end: false },
    { to: '/add-game', label: 'Adicionar Jogo', icon: Plus, adminOnly: true, end: true },
    { to: '/connections', label: 'Amigos', icon: Users, adminOnly: false, end: true },
    { to: '/messages', label: 'Mensagens', icon: MessageSquare, adminOnly: false, end: false },
    { to: '/profile', label: 'Configurações', icon: Settings, adminOnly: false, end: false },
  ];

  // Função isActive para os links do HEADER (que usam <Link> e não <NavLink>)
  const isActiveHeaderLink = (path: string) => {
    // Lista de caminhos pai que devem ter correspondência exata para não ficarem
    // ativos quando uma de suas sub-rotas específicas (com link próprio) estiver ativa.
    const parentPathsThatNeedExactMatch = ['/profile', '/collection'];

    if (parentPathsThatNeedExactMatch.includes(path)) {
      return location.pathname === path;
    }

    // A rota raiz '/' deve ser sempre exata.
    if (path === '/') {
      return location.pathname === '/';
    }

    // Comportamento padrão: ativo se exato ou se for um prefixo da rota atual.
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Função para fazer logout
  const handleSignOut = () => {
    signOut();
  };

  // Buscar solicitações pendentes e mensagens não lidas
  useEffect(() => {
    if (user?.id) {
      const fetchNotifications = async () => {
        try {
          const pendingCount = await communityService.getPendingConnectionRequestCount(user.id);
          setPendingRequests(pendingCount);
          const unreadCount = await communityService.getUnreadMessageCount(user.id);
          setUnreadMessages(unreadCount);
        } catch (error) {
          console.error('Erro ao buscar notificações:', error);
        }
      };
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 60000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  // Fechar menu de usuário quando clicar fora dele
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* SEO global padrão para todas as páginas */}
      <SEO 
        title="Boardverso - Plataforma para Jogos de Tabuleiro"
        description="Descubra, colecione e compartilhe jogos de tabuleiro na maior comunidade do Brasil"
      />
      
      <header className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-md">
        <div className="w-full px-2 md:px-4 lg:px-6 xl:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Link to="/" className="flex items-center">
                  <img src="/logo-boardverso.png" alt="Boardverso Logo" className="h-8 w-8 mr-2" />
                  <span className="font-semibold text-xl text-indigo-600 dark:text-indigo-400 font-poppins">Boardverso</span>
                </Link>
              </div>
              <nav className="hidden md:ml-6 md:flex md:space-x-4">
                <Link
                  to="/"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActiveHeaderLink('/') ? 'border-indigo-500 dark:border-indigo-400 text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Início
                </Link>
                <Link
                  to="/search"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActiveHeaderLink('/search') ? 'border-indigo-500 dark:border-indigo-400 text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Buscar
                </Link>
                <Link
                  to="/ranking"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActiveHeaderLink('/ranking') ? 'border-indigo-500 dark:border-indigo-400 text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Ranking
                </Link>
                <Link
                  to="/stores"
                  className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                    isActiveHeaderLink('/stores') ? 'border-indigo-500 dark:border-indigo-400 text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  Lojas Parceiras
                </Link>
                {isAuthenticated && (
                  <Link
                    to="/market"
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isActiveHeaderLink('/market') ? 'border-indigo-500 dark:border-indigo-400 text-gray-900 dark:text-gray-100' : 'border-transparent text-gray-500 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    Mercado
                  </Link>
                )}
              </nav>
            </div>
            <div className="flex items-center">
              {isAuthenticated ? (
                <div className="flex items-center ml-4 md:ml-0">
                  <button
                    onClick={toggleTheme}
                    className="mr-4 p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300 focus:outline-none"
                    aria-label={theme === 'dark' ? 'Ativar modo claro' : 'Ativar modo escuro'}
                  >
                    {theme === 'dark' ? (
                      <Sun className="h-6 w-6" />
                    ) : (
                      <Moon className="h-6 w-6" />
                    )}
                  </button>

                  <Link
                    to="/connections"
                    className="mr-4 relative flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    <Users className="h-6 w-6" />
                    {pendingRequests > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {pendingRequests}
                      </span>
                    )}
                  </Link>
                  
                  <Link
                    to="/messages"
                    className="mr-4 relative flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                  >
                    <MessageSquare className="h-6 w-6" />
                    {unreadMessages > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {unreadMessages}
                      </span>
                    )}
                  </Link>
                  
                  <div className="relative flex-shrink-0" ref={userMenuRef}>
                    <div>
                      <button
                        onClick={() => setUserMenuOpen(!userMenuOpen)}
                        className="bg-white dark:bg-gray-700 rounded-full flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
                      >
                        <span className="sr-only">Abrir menu</span>
                        <div className="h-8 w-8 rounded-full flex items-center justify-center bg-indigo-100 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200">
                          {user?.avatar_url ? (
                            <img 
                              src={user.avatar_url} 
                              alt={`Avatar de ${user.username || 'Usuário'}`}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            user?.full_name?.[0] || user?.username?.[0] || 'U'
                          )}
                        </div>
                      </button>
                    </div>
                    {userMenuOpen && (
                      <div className="origin-top-right absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black dark:ring-gray-700 ring-opacity-5 z-50 max-h-[80vh] overflow-y-auto focus:outline-none">
                        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
                          <p className="font-medium text-sm truncate text-gray-900 dark:text-gray-100">{user?.username || 'Usuário'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                        </div>
                        
                        <AccordionItem title="Navegação">
                          <Link to="/" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Início</Link>
                          <Link to="/search" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Buscar</Link>
                          <Link to="/feed" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Feed</Link>
                          <Link to="/ranking" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Ranking</Link>
                          <Link to="/recommendations" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Recomendações</Link>
                          <Link to="/stores" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Lojas Parceiras</Link>
                          <Link to="/market" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Mercado</Link>
                        </AccordionItem>

                        <AccordionItem title="Minha Atividade">
                          {user?.username && (
                             <Link
                               to={`/profile/${user.username}`}
                               className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                               onClick={() => setUserMenuOpen(false)}
                             >
                               Ver Meu Perfil Público
                             </Link>
                          )}
                          <Link to="/collection" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Minha Coleção</Link>
                          <Link to="/wishlist" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Lista de Desejos</Link>
                          <Link to="/collection/played" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Jogos Jogados</Link>
                          <Link to="/connections" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>
                            Amigos
                            {pendingRequests > 0 && (
                              <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                {pendingRequests}
                              </span>
                            )}
                          </Link>
                          <Link to="/messages" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>
                            Mensagens
                            {unreadMessages > 0 && (
                              <span className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                {unreadMessages}
                              </span>
                            )}
                          </Link>
                          <Link to="/feedback" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Meus Feedbacks</Link>
                        </AccordionItem>

                        <AccordionItem title="Meu Progresso">
                          <Link to="/profile/xp-history" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Meu Histórico de XP</Link>
                          <Link to="/profile/missions" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Minhas Missões</Link>
                          <Link to="/my-badges" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Minhas Badges</Link>
                          <Link to="/profile/choose-mascot" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Escolher Mascote</Link>
                        </AccordionItem>
                        
                        {isAdmin && (
                          <AccordionItem title="Admin">
                            <Link to="/admin" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Painel Administrativo</Link>
                            <Link to="/add-game" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Adicionar Jogo</Link>
                          </AccordionItem>
                        )}
                        
                        {/* Links de Conta fora do Acordeão, mas com divisor */}
                        <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                        <div className="py-1">
                           <span className="block px-4 pt-2 pb-1 text-xs text-gray-400 dark:text-gray-500 uppercase">Conta</span>
                          <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Configurações</Link>
                          <Link to="/integrations" className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setUserMenuOpen(false)}>Integrações</Link>
                          <button
                            onClick={handleSignOut}
                            className="block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Sair
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Link 
                  to="/signin" 
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 flex items-center ml-4"
                >
                  <LogOut className="h-5 w-5 mr-1" />
                  Entrar
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {isAuthenticated && (
          <div className="hidden md:flex w-64 bg-white dark:bg-gray-800 shadow-md dark:shadow-lg flex-col">
            <div className="py-6 flex flex-col flex-1">
              <nav className="flex-1 px-4 space-y-1">
                {/* Seção: Navegação Principal */}
                <span className="block px-2 pt-2 pb-1 text-xs text-gray-400 dark:text-gray-500 uppercase">Navegação</span>
                {navLinks.map(link => (
                  (!link.requiresAuth || isAuthenticated) && (
                    <Link 
                      key={link.to}
                      to={link.to}
                      className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink(link.to) ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                      <link.icon className={`mr-3 h-5 w-5 ${isActiveHeaderLink(link.to) ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />
                      {link.label}
                    </Link>
                  )
                ))}
                
                <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                {/* Seção: Minha Atividade & Gamificação */}
                <span className="block px-2 pt-2 pb-1 text-xs text-gray-400 dark:text-gray-500 uppercase">Minha Atividade</span>
                {authLinks.filter(link => !link.adminOnly && !['/profile', '/admin', '/add-game'].includes(link.to)).map(link => (
                  <Link 
                    key={link.to}
                    to={link.to}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink(link.to) ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                    <link.icon className={`mr-3 h-5 w-5 ${isActiveHeaderLink(link.to) ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />
                    {link.label}
                    {link.to === '/messages' && unreadMessages > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadMessages}</span>
                    )}
                    {link.to === '/connections' && pendingRequests > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{pendingRequests}</span>
                    )}
                  </Link>
                ))}
                {/* Links de Gamificação específicos para a sidebar */}
                <div className="my-2 border-t border-gray-200 dark:border-gray-700"></div>
                <span className="block px-2 pt-2 pb-1 text-xs text-gray-400 dark:text-gray-500 uppercase">Meu Progresso</span>
                <Link to="/profile/xp-history" className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink("/profile/xp-history") ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}><TrendingUp className={`mr-3 h-5 w-5 ${isActiveHeaderLink("/profile/xp-history") ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />Meu Progresso</Link>
                <Link to="/profile/missions" className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink("/profile/missions") ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}><Award className={`mr-3 h-5 w-5 ${isActiveHeaderLink("/profile/missions") ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />Minhas Missões</Link>
                <Link to="/my-badges" className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink("/my-badges") ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}><ShieldCheck className={`mr-3 h-5 w-5 ${isActiveHeaderLink("/my-badges") ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />Minhas Badges</Link>
                <Link to="/profile/choose-mascot" className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink("/profile/choose-mascot") ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}><User className={`mr-3 h-5 w-5 ${isActiveHeaderLink("/profile/choose-mascot") ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />Escolher Mascote</Link>

                {isAdmin && (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                    {/* Seção: Admin */}
                    <span className="block px-2 pt-2 pb-1 text-xs text-gray-400 dark:text-gray-500 uppercase">Admin</span>
                    {authLinks.filter(link => link.adminOnly && !['/profile'].includes(link.to)).map(link => (
                      <Link 
                        key={link.to}
                        to={link.to}
                        className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink(link.to) ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                        <link.icon className={`mr-3 h-5 w-5 ${isActiveHeaderLink(link.to) ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />
                        {link.label}
                      </Link>
                    ))}
                  </>
                )}

                <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                {/* Seção: Conta */}
                <span className="block px-2 pt-2 pb-1 text-xs text-gray-400 dark:text-gray-500 uppercase">Conta</span>
                <Link 
                  to="/profile" 
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink('/profile') ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                  <Settings className={`mr-3 h-5 w-5 ${isActiveHeaderLink('/profile') ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />
                  Configurações
                </Link>
                <Link 
                  to="/integrations" 
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${isActiveHeaderLink('/integrations') ? 'bg-indigo-100 dark:bg-indigo-900 text-indigo-700 dark:text-indigo-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}`}>
                  <Puzzle className={`mr-3 h-5 w-5 ${isActiveHeaderLink('/integrations') ? 'text-indigo-500 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500 group-hover:text-gray-500 dark:group-hover:text-gray-400'}`} />
                  Integrações
                </Link>
              </nav>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 bg-gray-100 dark:bg-gray-900 overflow-x-hidden overflow-y-auto">
          {children}
        </main>
      </div>

      <footer className="bg-gray-800 dark:bg-gray-900 text-white py-4">
        <div className="container mx-auto px-4">
          <p className="text-center text-sm text-gray-300 dark:text-gray-400">
            &copy; {new Date().getFullYear()} Boardverso. Todos os direitos reservados.
          </p>
          <div className="flex justify-center mt-2 space-x-4 text-xs">
            <Link to="/privacy-policy" className="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-gray-200">
              Política de Privacidade
            </Link>
            <Link to="/terms-of-service" className="text-gray-300 dark:text-gray-400 hover:text-white dark:hover:text-gray-200">
              Termos de Serviço
            </Link>
          </div>
        </div>
      </footer>

      {/* Barra de Navegação Inferior Fixa (Mobile) */}
      {isAuthenticated && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-indigo-50 dark:bg-gray-800 border-t border-indigo-200 dark:border-gray-700 shadow-md dark:shadow-lg z-50 rounded-t-lg">
          <nav className="flex justify-around items-center h-16">
            <NavLink 
              key="/feed"
              to="/feed" 
              end
              className={({ isActive }) => `flex flex-col items-center justify-center w-full h-full text-xs font-medium transition-colors ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400'}`}
            >
              <Newspaper className="h-5 w-5 mb-1" />
              Feed
            </NavLink>

            <NavLink 
              key="/profile/xp-history"
              to="/profile/xp-history" 
              end
              className={({ isActive }) => `flex flex-col items-center justify-center w-full h-full text-xs font-medium transition-colors ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400'}`}
            >
              <TrendingUp className="h-5 w-5 mb-1" />
              Progresso
            </NavLink>

            <NavLink 
              key="/collection"
              to="/collection" 
              end
              className={({ isActive }) => `flex flex-col items-center justify-center w-full h-full text-xs font-medium transition-colors ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400'}`}
            >
              <LayoutGrid className="h-5 w-5 mb-1" />
              Coleção
            </NavLink>

            <NavLink 
              key="/collection/played"
              to="/collection/played"
              end
              className={({ isActive }) => `flex flex-col items-center justify-center w-full h-full text-xs font-medium transition-colors ${isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400'}`}
            >
              <PlusCircle className="h-5 w-5 mb-1" />
              Partidas
            </NavLink>
          </nav>
        </div>
      )}
    </div>
  );
} 